// En TaskDetailModal.jsx
import React, { useState, useRef, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { useTaskContext } from "../context/TaskContext";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Avatar from "../components/Avatar";
import { useAuth } from "../context/AuthContext";

const customStyles2 = {
  content: {
    top: "50%",
    left: "50%",
    width: "50%",
    maxWidth: "600px",
    maxHeight: "80%",
    transform: "translate(-50%, -50%)",
    overflowY: "auto",
    padding: "40px",
    borderRadius: "10px",
    border: "none",
    background: "#ffffff",
    position: "relative",
    boxShadow: "0 10px 50px rgba(0, 0, 0, 0.2)",
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    /* Ocultar scrollbar en IE y Edge */
    msOverflowStyle: "none",
    /* Ocultar scrollbar en Firefox */
    scrollbarWidth: "none",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1008,
  },
  inputContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    marginBottom: "20px",
  },
  label: {
    display: "flex",
    flexDirection: "column",
    fontSize: "16px",
    color: "#333",
    marginBottom: "5px",
  },
  input: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    marginTop: "10px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  inputColor: {
    appearance: "none",
    border: "1px solid #ccc",
    height: "35px",
    width: "100%",
    borderRadius: "5px",
    cursor: "pointer",
    padding: "0",
    transition: "box-shadow 0.3s ease",
    marginTop: "10px",
  },
  addButton: {
    padding: "15px",
    borderRadius: "10px",
    border: "none",
    backgroundColor: "#007bff",
    color: "#ffffff",
    fontSize: "16px",
    cursor: "pointer",
    transition: "background-color 0.3s, transform 0.2s",
  },
};

const TaskDetailModal = ({
  isOpen,
  onRequestClose,
  selectedTask, // Este viene de afuera
}) => {
  const { user } = useAuth();
  const { formatDate } = useFunctionsPrevia();
  const [localTask, setLocalTask] = useState(null); // <-- Estado local de la tarea
  const [comment, setComment] = useState("");
  const [editCommentIndex, setEditCommentIndex] = useState(-1);
  const [editedComment, setEditedComment] = useState("");
  const editCommentRef = useRef(null);

  const { updateTask } = useTaskContext();

  useEffect(() => {
    if (selectedTask && isOpen) {
      setLocalTask({ ...selectedTask });
    }
  }, [selectedTask, isOpen]);

  if (!localTask) return null;

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    // Crea el objeto comentario
    const newComment = {
      text: comment,
      userName: user.displayName, // o localTask.userName
      userId: user.uid,
      timestamp: new Date().toISOString(),
    };
    // Actualiza la tarea en Firestore
    const updatedComments = [...(localTask.comments || []), newComment];
    await updateTask(
      localTask.id,
      { comments: updatedComments },
      "Comentario agregado"
    );

    // Actualiza también la copia local para ver el cambio inmediato
    setLocalTask({
      ...localTask,
      comments: updatedComments,
    });

    setComment("");
  };

  const handleEditCommentToggle = (index) => {
    setEditCommentIndex(index);
    setEditedComment(localTask.comments[index].text);
  };

  const handleSaveEditedComment = async (index) => {
    const updatedComments = [...localTask.comments];
    updatedComments[index] = {
      ...updatedComments[index],
      text: editedComment,
      timestamp: new Date().toISOString(),
    };

    await updateTask(localTask.id, { comments: updatedComments });
    setLocalTask({
      ...localTask,
      comments: updatedComments,
    });
    setEditCommentIndex(-1);
    setEditedComment("");
  };

  const handleDeleteComment = async (index) => {
    // 1️⃣ Pregunta de confirmación
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar este comentario?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    // 2️⃣ Si confirma, filtra comentarios y actualiza
    const updatedComments = localTask.comments.filter((_, i) => i !== index);
    await updateTask(
      localTask.id,
      { comments: updatedComments },
      "Comentario eliminado"
    );

    // 3️⃣ Refleja el cambio en el estado local
    setLocalTask({
      ...localTask,
      comments: updatedComments,
    });
  };

  const handleCancelEdit = () => {
    setEditCommentIndex(-1);
    setEditedComment("");
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={customStyles2}
      contentLabel="Detalles de la Tarea"
    >
      <div>
        <button
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "#EAEDF0",
            color: "#44566C",
            borderRadius: "50%",
            height: 32,
            width: 32,
            border: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            lineHeight: 0,
            fontSize: 16,
          }}
          onClick={onRequestClose}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div className="task-details">
        <div className="user-comment-container">
          <div className="user">
            <Avatar userId={user.uid} avatarUrl={user.photoURL} size={40} />
            <div className="user-info">
              <div className="name">{localTask.user}</div>
              <div className="time">{formatDate(localTask.date)}</div>
            </div>
          </div>

          <div className="comments-container">
            <h4 style={{ fontFamily: "Inter, sans-serif" }}>
              Comentarios:
            </h4>
            {localTask.comments && localTask.comments.length > 0 ? (
              localTask.comments.map((c, index) => (
                <div key={index} className="comment">
                  <div className="user">
                    <Avatar
                      userId={c.userId}
                      size={32}
                      className="comment-avatar"
                    />
                    <div className="name">{c.userName}</div>
                    <div className="time">{formatDate(c.timestamp)}</div>
                  </div>
                  {editCommentIndex === index ? (
                    <input
                      type="text"
                      value={editedComment}
                      onChange={(e) => setEditedComment(e.target.value)}
                      className="custom-input"
                      ref={editCommentRef}
                    />
                  ) : (
                    <div className="comentario">{c.text}</div>
                  )}
                  {editCommentIndex === index ? (
                    <div className="comment-buttons-container">
                      <button
                        className="comment-button"
                        onClick={() => handleSaveEditedComment(index)}
                      >
                        Guardar
                      </button>
                      <button
                        className="comment-button"
                        onClick={handleCancelEdit}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="comment-buttons-container">
                      <button
                        className="comment-button"
                        onClick={() => handleEditCommentToggle(index)}
                      >
                        Editar
                      </button>
                      <button
                        className="comment-button"
                        onClick={() => handleDeleteComment(index)}
                      >
                        Eliminar
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No hay comentarios.</p>
            )}

            <div className="write-comment">
              <input
                type="text"
                placeholder="Haz un comentario..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleAddComment();
                }}
              />
              <button className="send-button" onClick={handleAddComment}>
                Enviar
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TaskDetailModal;
